import { useEffect, useRef, useState } from "react";
import { Flame } from "lucide-react";
import { HomeFeedDrawer } from "./HomeFeedDrawer";
import { pingStreak, loadStreak } from "@/lib/retention/streaks";
import { canClaimToday } from "@/lib/retention/dailyCheckIn";
import { useCleanMode, useKeyboardOpen } from "@/lib/uiOverlays";
import { useFocusModePrefs } from "@/lib/mobileOverlayManager";
import { useFloatingControlVisible } from "@/lib/mobileFloatingControls";
import { useDraggableFab } from "@/lib/pr27/draggableFab";
import { subscribePeek, type PeekReason } from "@/lib/retention/homeFeedAutoPeek";
import type { PublicEmotionalCtx } from "@/hooks/usePublicEmotionalSignal";
import { DailyWowSplash } from "@/components/v4/DailyWowSplash";
import { PremiumGoalCelebration } from "@/components/v4/PremiumGoalCelebration";
import { VictoryCardPrompt } from "@/components/viral/VictoryCardPrompt";
import { trackEvent } from "@/lib/audit";
import { registerFab, useFabVisible } from "@/lib/ui/fabOrchestrator";
import { useLayerOffset, OVERLAP_HEIGHTS } from "@/lib/pr30/zeroOverlapPolicy";

export function RetentionFab({
  matchMinute,
  isLive,
  emotionalCtx,
}: {
  matchMinute?: number;
  isLive?: boolean;
  emotionalCtx?: PublicEmotionalCtx;
}) {
  const [open, setOpen] = useState(false);
  const [peek, setPeek] = useState<PeekReason | null>(null);
  const peekTimer = useRef<number | null>(null);
  const cleanMode = useCleanMode();
  const keyboardOpen = useKeyboardOpen();
  const { focusActive, prefs } = useFocusModePrefs();
  const muted = focusActive && prefs.retention === false;
  const dockVisible = useFloatingControlVisible("retention_fab");
  const drag = useDraggableFab("retention_fab");
  // P0-MOBILE-OVERLAY-01 — stack above BottomNav + MobileShareDock +
  // MundialPredictionFloatingCard via PR-30 Zero Overlap Policy.
  // fab_secondary offset = bottom_nav + floating_cta (when each is active).
  // We add the fab_secondary slot height so we sit clearly above the card.
  const stackOffset = useLayerOffset("fab_secondary");
  const mobileBottom = stackOffset + OVERLAP_HEIGHTS.fab_secondary + 24;
  useEffect(() => { pingStreak(); }, []);
  // P0-RETENTION-01 — coordinate with the FAB orchestrator.
  useEffect(() => registerFab("retention_fab"), []);
  const fabAllowed = useFabVisible("retention_fab");

  // PR-DISCOVERY-01 · P0-2 — surface pending state on the FAB so users
  // know there's something to claim. Recomputes when the drawer opens
  // (claim sites all live inside) and on a low-frequency tick.
  const [pending, setPending] = useState<{ checkin: boolean; streak: number }>(
    () => ({ checkin: false, streak: 0 })
  );
  useEffect(() => {
    const recompute = () => {
      try {
        setPending({ checkin: canClaimToday(), streak: loadStreak().consecutive_days_active });
      } catch { /* noop */ }
    };
    recompute();
    const id = window.setInterval(recompute, 60_000);
    const onClose = () => recompute();
    window.addEventListener("visibilitychange", onClose);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("visibilitychange", onClose);
    };
  }, [open]);
  const showBadge = !open && (pending.checkin || pending.streak >= 2);
  const badgeLabel = pending.checkin ? "!" : String(Math.min(99, pending.streak));
  useEffect(() => {
    if (showBadge) void trackEvent("retention_fab_badge_shown", {
      checkin: pending.checkin, streak: pending.streak,
    });
  }, [showBadge, pending.checkin, pending.streak]);
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("ss2026:open-feed", h as EventListener);
    window.addEventListener("ss2026:open-missions", h as EventListener);
    return () => {
      window.removeEventListener("ss2026:open-feed", h as EventListener);
      window.removeEventListener("ss2026:open-missions", h as EventListener);
    };
  }, []);
  useEffect(() => {
    return subscribePeek((reason) => {
      if (open) return; // already discovered
      setPeek(reason);
      if (peekTimer.current) window.clearTimeout(peekTimer.current);
      peekTimer.current = window.setTimeout(() => setPeek(null), 5500);
    });
  }, [open]);
  useEffect(() => () => { if (peekTimer.current) window.clearTimeout(peekTimer.current); }, []);
  return (
    <>
      <button
        ref={drag.ref}
        onPointerDown={drag.onPointerDown}
        onClick={() => { if (!drag.isDragging) { setOpen(true); setPeek(null); } }}
        style={{
          // Apply the dynamic mobile bottom unless the user has dragged
          // the FAB to a custom position (drag.style sets `position:fixed`
          // with explicit left/top). Always keep drag.touchAction.
          ...(drag.style.position === "fixed"
            ? drag.style
            : { ...drag.style, bottom: `calc(${mobileBottom}px + env(safe-area-inset-bottom))` }),
        }}
        aria-label={pending.checkin ? "Daily feed — recompensa pendiente" : "Daily feed"}
        aria-hidden={cleanMode || keyboardOpen || muted || !dockVisible || !fabAllowed}
        data-peek={peek ?? "false"}
        className={
          "fixed right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[oklch(0.78_0.2_50)] text-black shadow-lg transition hover:scale-105 md:bottom-24 md:right-6 " +
          (peek ? "ring-2 ring-[oklch(0.78_0.2_50)]/70 animate-pulse " : "") +
          (cleanMode || keyboardOpen || muted || !dockVisible || !fabAllowed
            ? "pointer-events-none opacity-0"
            : "opacity-100")
        }
      >
        <Flame className="h-5 w-5" />
        {peek && (
          <span
            aria-hidden
            className="pointer-events-none absolute -top-1 -right-1 h-3 w-3 rounded-full border border-background bg-[oklch(0.78_0.2_50)]"
          />
        )}
        {showBadge && !peek && (
          <span
            aria-hidden
            className="pointer-events-none absolute -top-1 -right-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full border border-background bg-primary px-1 text-[10px] font-black leading-none text-primary-foreground shadow"
          >
            {badgeLabel}
          </span>
        )}
      </button>
      <HomeFeedDrawer
        open={open}
        onClose={() => setOpen(false)}
        matchMinute={matchMinute}
        isLive={isLive}
        emotionalCtx={emotionalCtx}
      />
      <DailyWowSplash />
      <PremiumGoalCelebration />
      <VictoryCardPrompt />
    </>
  );
}